// Client telemetry for the embedded widget (AIS-5357).
//
// Everything between "a visitor clicked the button" and "audio is flowing"
// happens in a browser we cannot reach. The server sees a session mint and,
// if the call connects, a conversation — it cannot see a denied microphone
// prompt, a bundle that never loaded, a blocked autoplay, or a visitor who
// gave up during connect. Those are the failures that cost a customer a
// conversation, and they were invisible in every dashboard we own.
//
// What is reported is the complete list in TELEMETRY_EVENTS below, plus an
// error code from the widget's own taxonomy, milliseconds since mount, and
// the ids needed to correlate them. What is deliberately never reported: the
// page URL, the page title, anything the visitor typed or said, any
// transcript, and any identifier that could link one visit to another or one
// site to another. The embedding origin is not sent either — the server reads
// it from the request header, where the browser attests it.
//
// The transport is navigator.sendBeacon, so a report survives the page
// unloading, which is exactly when the interesting "they gave up" events
// fire. The response is discarded and nothing is ever retried: this is
// diagnostic data, and a retry loop against an unauthenticated endpoint is
// an amplifier, not a feature.

/** The closed event vocabulary. Pinned to the Go ingest allowlist by a test. */
export const TELEMETRY_EVENTS = [
  "widget.mounted",
  "widget.call_requested",
  "widget.mic_denied",
  "widget.session_minted",
  "widget.session_failed",
  "widget.connected",
  "widget.first_audio",
  "widget.audio_blocked",
  "widget.ended",
  "widget.error",
] as const;

export type TelemetryEventName = (typeof TELEMETRY_EVENTS)[number];

interface QueuedEvent {
  name: TelemetryEventName;
  conversation_id?: string;
  error_code?: string;
  elapsed_ms: number;
}

/** Flush once a batch reaches this size. Stays under the server's cap of 20. */
const BATCH_MAX = 10;

/**
 * Hard ceiling on the in-memory queue. A page that somehow generates events
 * faster than they flush drops the oldest rather than growing without bound
 * on a site we do not control.
 */
const QUEUE_MAX = 50;

/** Idle delay before flushing a partial batch, so a long-lived page still reports. */
const FLUSH_DEBOUNCE_MS = 5_000;

const OPT_OUT_KEY_PREFIX = "speechify-agent:telemetry-off:";

/**
 * True when the visitor's browser advertises Global Privacy Control. GPC is
 * the live, legally-recognised signal; DNT is deliberately not consulted
 * because browsers ship it enabled by default in configurations where the
 * user expressed nothing, which makes it a statement about the vendor rather
 * than the visitor.
 */
function privacySignalSet(): boolean {
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

/**
 * Reads the remembered opt-out for an agent.
 *
 * The stored value is the single bit "this agent turned telemetry off". It
 * holds no identifier, is never read by anything else, and exists only so a
 * later widget-config failure cannot resurrect reporting for a customer who
 * switched it off. Storage is unavailable in a sandboxed iframe and throws
 * outright under some privacy settings, so every access is guarded and a
 * failure degrades to "not remembered" rather than breaking the widget.
 */
function readOptOut(agentId: string): boolean {
  try {
    return localStorage.getItem(OPT_OUT_KEY_PREFIX + agentId) === "1";
  } catch {
    return false;
  }
}

function writeOptOut(agentId: string): void {
  try {
    localStorage.setItem(OPT_OUT_KEY_PREFIX + agentId, "1");
  } catch {
    // Storage denied. The opt-out still holds for this page-load; it just
    // will not be remembered for the next one.
  }
}

/** Per-page-load correlation id. Not persisted, not reused, not an identity. */
function newSessionId(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  if (c?.getRandomValues) {
    const buf = new Uint8Array(16);
    c.getRandomValues(buf);
    return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export interface TelemetryOptions {
  /** Origin the events POST to. Same base the widget uses for every call. */
  apiBase: string;
  /** Bundle build stamp, reported so a failure spike can be pinned to a build. */
  bundleVersion: string;
  /** Test seam. Defaults to the real beacon transport. */
  transport?: (url: string, body: string) => void;
}

type State = "buffering" | "on" | "off";

/**
 * Beacon transport. sendBeacon is preferred because it is the only thing
 * browsers guarantee to deliver from a page that is going away; text/plain
 * keeps it a CORS-simple request so no preflight is needed during unload
 * (the server parses the body as JSON regardless). It returns false when the
 * browser's queue is full, in which case a keepalive fetch is the fallback.
 */
function beacon(url: string, body: string): void {
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
      if (navigator.sendBeacon(url, blob)) return;
    }
    void fetch(url, {
      method: "POST",
      body,
      keepalive: true,
      credentials: "omit",
      mode: "cors",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
    }).catch(() => {});
  } catch {
    // Telemetry must never be able to break the widget it reports on.
  }
}

export class Telemetry {
  readonly #apiBase: string;
  readonly #bundleVersion: string;
  readonly #transport: (url: string, body: string) => void;
  readonly #sessionId = newSessionId();
  readonly #mountedAt = performance.now();

  // Three independent reasons reporting can be off, kept separate rather
  // than collapsed into one flag so each behaves the way it should: the
  // embedder's attribute is a live control they can toggle both ways, while
  // the browser's privacy signal and an observed opt-out are one-way — once
  // either is true nothing turns reporting back on for this page-load.
  #attributeOptOut = false;
  readonly #privacyOptOut = privacySignalSet();
  #observedOptOut = false;
  /**
   * Set once the agent's remote setting has been resolved one way or the
   * other. Nothing is sent before it: the whole reason a first page-load
   * cannot leak past an opt-out is that the widget waits for the answer
   * rather than racing a timer against it.
   *
   * There is deliberately no deadline here. The obvious design - hold for N
   * seconds, then send anyway - loses the race whenever widget-config is
   * slower than N on a first visit, which is the one visit where nothing is
   * remembered locally yet. Instead the element tells us when the config
   * settled, in either direction, and `fetchWithTimeout` guarantees it
   * always settles. A bug that broke that wiring would make the widget
   * silent rather than talkative, which is the correct direction to fail.
   */
  #released = false;

  #agentId = "";
  #conversationId = "";
  #queue: QueuedEvent[] = [];
  #flushTimer: ReturnType<typeof setTimeout> | undefined;
  #hiddenListener: (() => void) | null = null;
  #pageHideListener: (() => void) | null = null;

  constructor(opts: TelemetryOptions) {
    this.#apiBase = opts.apiBase;
    this.#bundleVersion = opts.bundleVersion;
    this.#transport = opts.transport ?? beacon;
    this.#listenForUnload();
  }

  get #state(): State {
    if (this.#attributeOptOut || this.#privacyOptOut || this.#observedOptOut) return "off";
    return this.#released ? "on" : "buffering";
  }

  /**
   * Applies the embedder's `telemetry` attribute. Reversible, because it is
   * an attribute they can remove again; it never re-enables reporting that
   * the browser or the agent's own setting has turned off.
   */
  setLocalOptOut(off: boolean): void {
    if (this.#attributeOptOut === off) return;
    this.#attributeOptOut = off;
    if (off) this.#dropQueued();
  }

  /**
   * Sets the agent under report and re-checks its remembered opt-out.
   *
   * Switching agents re-buffers: the setting is per agent, so an answer
   * resolved for the previous one says nothing about this one, and carrying
   * it over would report for an agent whose opt-out has not been read yet.
   * An SPA that reuses one element across routes does exactly this.
   */
  setAgentId(agentId: string | undefined): void {
    const next = agentId ?? "";
    if (next !== this.#agentId) this.#released = false;
    this.#agentId = next;
    if (this.#agentId && readOptOut(this.#agentId)) {
      this.#observedOptOut = true;
      this.#dropQueued();
    }
  }

  setConversationId(conversationId: string | undefined): void {
    this.#conversationId = conversationId ?? "";
  }

  /**
   * Applies the agent's server-side setting once widget-config resolves.
   * Disabling is remembered so it holds from the next page-load onward even
   * if that fetch never succeeds again.
   */
  applyRemoteConfig(enabled: boolean): void {
    if (!enabled) {
      if (this.#agentId) writeOptOut(this.#agentId);
      this.#observedOptOut = true;
      this.#dropQueued();
      return;
    }
    this.#released = true;
    this.#flush();
  }

  /**
   * Called when the agent's setting could not be read at all - the config
   * request failed, or the embed has no agent to read one for (a
   * session-token embed). Reporting proceeds: a widget-config fetch that
   * fails is precisely the situation whose telemetry we most want, and
   * refusing to report it would blind us exactly when something is broken.
   * A customer who actually opted out is still covered, because the answer
   * was remembered locally the first time it was seen.
   */
  configUnavailable(): void {
    this.#released = true;
    this.#flush();
  }

  record(name: TelemetryEventName, extra?: { errorCode?: string }): void {
    const state = this.#state;
    if (state === "off") return;
    this.#queue.push({
      name,
      conversation_id: this.#conversationId || undefined,
      error_code: extra?.errorCode || undefined,
      elapsed_ms: Math.max(0, Math.round(performance.now() - this.#mountedAt)),
    });
    if (this.#queue.length > QUEUE_MAX) this.#queue.splice(0, this.#queue.length - QUEUE_MAX);
    if (state !== "on") return;
    if (this.#queue.length >= BATCH_MAX) {
      this.#flush();
      return;
    }
    this.#armFlush();
  }

  /** Sends whatever is queued now. Safe to call when there is nothing to send. */
  flush(): void {
    this.#flush();
  }

  /** Releases listeners and timers, sending anything still queued. */
  dispose(): void {
    this.flush();
    this.#clearFlushTimer();
    if (this.#hiddenListener) {
      document.removeEventListener("visibilitychange", this.#hiddenListener);
      this.#hiddenListener = null;
    }
    if (this.#pageHideListener) {
      window.removeEventListener("pagehide", this.#pageHideListener);
      this.#pageHideListener = null;
    }
  }

  #dropQueued(): void {
    this.#queue = [];
    this.#clearFlushTimer();
  }

  #armFlush(): void {
    if (this.#flushTimer !== undefined) return;
    this.#flushTimer = setTimeout(() => {
      this.#flushTimer = undefined;
      this.#flush();
    }, FLUSH_DEBOUNCE_MS);
  }

  #clearFlushTimer(): void {
    if (this.#flushTimer === undefined) return;
    clearTimeout(this.#flushTimer);
    this.#flushTimer = undefined;
  }

  #flush(): void {
    this.#clearFlushTimer();
    if (this.#state !== "on" || this.#queue.length === 0) return;
    const events = this.#queue.splice(0, BATCH_MAX);
    try {
      this.#transport(
        `${this.#apiBase}/v1/widget/events`,
        JSON.stringify({
          session_id: this.#sessionId,
          agent_id: this.#agentId || undefined,
          bundle_version: this.#bundleVersion,
          events,
        }),
      );
    } catch {
      // Reporting is a diagnostic. A transport that throws — a page that has
      // replaced fetch, a serialiser that choked — must not propagate into
      // the click handler or the unload listener that called this.
    }
    // A batch larger than one flush's worth is rare, but draining it here
    // rather than waiting for the next trigger keeps the unload path from
    // silently discarding the tail.
    if (this.#queue.length > 0) this.#flush();
  }

  /**
   * Flushes when the page is going away. Both signals are needed and they are
   * not interchangeable: pagehide is the reliable navigation signal, while
   * visibilitychange→hidden is the only one iOS Safari fires when an app is
   * backgrounded and later killed without ever navigating. pagehide flushes
   * unconditionally because visibilityState is not guaranteed to have flipped
   * to "hidden" by the time it runs.
   */
  #listenForUnload(): void {
    this.#hiddenListener = () => {
      if (document.visibilityState === "hidden") this.flush();
    };
    this.#pageHideListener = () => this.flush();
    document.addEventListener("visibilitychange", this.#hiddenListener);
    window.addEventListener("pagehide", this.#pageHideListener);
  }
}
